import React, { useState, useReducer, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MensajeCargando from './MensajeCargando.js';
import MensajeGrabando from './MensajeGrabando.js';
import MensajeTexto from './MensajeTexto.js';
import MensajeError from './MensajeError.js';
import NuevoItemFiltros from './NuevoItemFiltros.js';
import * as Reducer from '../reducers/ReducerFiltroNuevo.js';
import * as ReducerDA from '../reducers/ReducerDataAccess.js';
import styles from './AgregarItemFiltro.module.css';

/**
 * Componente genérico para agregar nuevos items (Club, Persona, etc.)
 *
 * Props:
 * - objeto: string - Tipo de objeto ('club', 'persona', etc.)
 * - campos: Array - Configuración de campos a mostrar
 *   { nombre: string, componente: ReactElement, titulo: string }
 * - onBuscar: Function - Función para buscar items existentes
 *   (filtros) => Promise<Array>
 * - onCrear: Function - Función para crear nuevo item
 *   (filtros) => Promise<number> (retorna el ID creado)
 * - onMostrarLista: Function - Callback cuando se muestra la lista
 *   (resultados) => void
 * - validarFiltros: Function - Validar filtros antes de buscar
 *   (filtros) => { valido: boolean, error: string }
 * - normalizarFiltros: Function - Normalizar filtros antes de enviar
 *   (filtros) => Object (opcional)
 * - extraerNombre: Function - Extraer nombre para verificación de duplicados
 *   (filtros) => string (opcional)
 * - autoAbrir: boolean - Abrir formulario automáticamente
 * - ocultarBotonAgregar: boolean - Ocultar botón agregar
 */
const AgregarItemFiltro = ({
  objeto = 'item',
  campos = [],
  onBuscar,
  onCrear,
  onMostrarLista,
  validarFiltros,
  normalizarFiltros,
  extraerNombre,
  autoAbrir = false,
  ocultarBotonAgregar = false,
  rutaDestino = null, // ej: '/club/'
}) => {
  const [reducer, setReducer] = useReducer(Reducer.reducerFiltroNuevo, Reducer.ReducerVacio);
  const [dataAccess, setDataAccess] = useReducer(
    ReducerDA.reducerDataAccess,
    ReducerDA.ReducerVacio
  );
  const [encontrados, setEncontrados] = useState(null);
  const [filtros, setFiltros] = useState({});
  const navigate = useNavigate();

  // Inicializar filtros con valores vacíos
  useEffect(() => {
    const filtrosInicial = {};
    campos.forEach((campo) => {
      filtrosInicial[campo.nombre] = campo.valorInicial ?? null;
    });
    setFiltros(filtrosInicial);
  }, [campos]);

  const onCampoChange = (campo, valor) => {
    if (reducer.mensaje || reducer.errores) {
      setReducer({ type: Reducer.Accion.LIMPIAR_MENSAJES, value: null });
    }
    if (!encontrados) {
      setEncontrados([]);
    }
    setFiltros({ ...filtros, [campo]: valor });
  };

  const onConfirmar = async () => {
    setDataAccess({ type: ReducerDA.Accion.GRABANDO, value: true });
    setReducer({ type: Reducer.Accion.CONFIRMAR, value: null });
    try {
      const filtrosNormalizados = normalizarFiltros ? normalizarFiltros(filtros) : filtros;
      const nuevoId = await onCrear(filtrosNormalizados);

      if (Number(nuevoId) > 0) {
        if (rutaDestino) {
          navigate(rutaDestino + nuevoId, { state: { nuevo: true } });
        } else {
          // Callback personalizado si no hay ruta
          setReducer({ type: Reducer.Accion.LIMPIAR_MENSAJES, value: null });
        }
      } else {
        setDataAccess({ type: ReducerDA.Accion.ERRORES, value: ['Error al grabar los datos'] });
      }
    } catch (error) {
      setDataAccess({ type: ReducerDA.Accion.ERRORES, value: [error.message] });
    }
  };

  const onNuevo = () => {
    const filtrosLimpios = {};
    campos.forEach((campo) => {
      filtrosLimpios[campo.nombre] = campo.valorInicial ?? null;
    });
    setFiltros(filtrosLimpios);
    setEncontrados(null);
    setReducer({ type: Reducer.Accion.NUEVO, value: null });
  };

  // Auto-abrir si se especifica
  useEffect(() => {
    if (autoAbrir) {
      const filtrosLimpios = {};
      campos.forEach((campo) => {
        filtrosLimpios[campo.nombre] = campo.valorInicial ?? null;
      });
      setFiltros(filtrosLimpios);
      setEncontrados(null);
      setReducer({ type: Reducer.Accion.NUEVO, value: null });
    }
  }, [autoAbrir, campos]);

  const onVerificar = async () => {
    setDataAccess({ type: ReducerDA.Accion.LIMPIAR, value: null });

    // Validar filtros
    if (validarFiltros) {
      const validacion = validarFiltros(filtros);
      if (!validacion.valido) {
        setReducer({ type: Reducer.Accion.ERROR_VERIFICAR, value: validacion.error });
        return;
      }
    }

    // Buscar
    setDataAccess({ type: ReducerDA.Accion.CARGANDO, value: true });
    try {
      const result = await onBuscar(filtros);
      if (result && result !== Promise.resolve(result)) {
        verificarEncontrados(result, extraerNombre ? extraerNombre(filtros) : '');
        onMostrarLista(result);
      }
      setDataAccess({ type: ReducerDA.Accion.LIMPIAR, value: null });
    } catch (error) {
      setDataAccess({ type: ReducerDA.Accion.ERRORES, value: [error.message] });
    }
  };

  const verificarEncontrados = (encontrados, nombreBuscado) => {
    if (encontrados.length === 0) {
      setReducer({
        type: Reducer.Accion.VERIFICADO,
        value: `¿Confirma que desea insertar el nuevo ${objeto}?`,
      });
    } else {
      let texto = '';
      if (encontrados.length === 1) {
        texto = `Se encontró un ${objeto} con datos similares`;
      } else {
        texto = `Se encontraron ${encontrados.length} ${objeto}s con datos similares`;
      }
      setReducer({
        type: Reducer.Accion.VERIFICADO,
        value: texto + `. ¿Confirma que desea insertar el nuevo ${objeto}?`,
      });
    }
  };

  // Renderizar campos dinámicamente
  const renderCampos = () => {
    return campos.map((campo) => (
      <React.Fragment key={campo.nombre}>
        {campo.titulo && <div className={`${styles.tituloCampo}`}>{campo.titulo}</div>}
        {React.cloneElement(campo.componente, {
          nombre: campo.nombre,
          valor: filtros[campo.nombre],
          editar: true,
          onChange: onCampoChange,
          ...(campo.estilo && { estilo: campo.estilo }),
        })}
      </React.Fragment>
    ));
  };

  return (
    <div className={`${styles.baseFiltros}`}>
      <NuevoItemFiltros
        objeto={objeto}
        estado={reducer.estado}
        onNuevo={onNuevo}
        onVerificar={onVerificar}
        onConfirmar={onConfirmar}
        ocultarBotonAgregar={ocultarBotonAgregar}
      >
        {renderCampos()}
      </NuevoItemFiltros>
      <MensajeCargando isCargando={dataAccess.buscando} estilo={`${styles.mensaje}`} />
      <MensajeGrabando
        isGrabando={dataAccess.grabando}
        mensaje={`Creando ${objeto}...`}
        estilo={`${styles.mensaje}`}
      />
      <MensajeTexto mensaje={reducer.mensaje} estilo={`${styles.mensaje}`} />
      <MensajeError errores={dataAccess.errores || reducer.errores} estilo={`${styles.mensaje}`} />
    </div>
  );
};

export default AgregarItemFiltro;
